/**
 * TemplateStepEditor — one step of a sequence (Figma "Edit template", node
 * 554:46177 / 560:50910): a collapsible card (envelope, "Message" or "Step N",
 * ⋯ menu, chevron) with the subject on one line and the body under a toolbar
 * on top; a later step is preceded by its "Wait n days" pill, which sits
 * between the cards.
 *
 * The two shapes come straight from the BE's ValidateSteps: step 1 is the
 * first touch (subject required, no delay), every later step is a follow-up
 * (positive delay, optional time of day, no subject — it goes in the same
 * thread as the first email).
 *
 * The delay is edited as a "Wait [n] [days|hours] ▾" picker rather than a
 * raw days field: `delayDays` stays the source of truth (a fractional day
 * count, per the BE column), and `unit` is local UI state that only decides
 * how the number on screen is read back into that fraction.
 */
import { useId, useState } from 'react';
import { CaretDown, CaretUp, Clock, Copy, DotsThree, Envelope, Sparkle, Trash, X } from '../../../design-system/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { CharCounter } from './CharCounter';
import { BODY_LIMIT, SUBJECT_LIMIT } from './draftCaps';
import { ComposerEditor, type ComposerEditorHandle } from './composerEditor/ComposerEditor';
import { MergeTagMenu } from './MergeTagMenu';
import { TemplateStepAiPanel } from './TemplateStepAiPanel';
import { FIRST_STEP_LINK_NOTE, type TemplateStepDraft } from './templateValidation';

export type TemplateStepField = 'subject' | 'body';
export type DelayUnit = 'days' | 'hours';

/** Whole days stay in days; anything else (under a day, or a fraction) reads in hours. */
export function delayToWait(delayDays?: number): { n: number; unit: DelayUnit } {
  if (delayDays === undefined || delayDays <= 0) return { n: 1, unit: 'days' };
  return Number.isInteger(delayDays) ? { n: delayDays, unit: 'days' } : { n: Math.round(delayDays * 24), unit: 'hours' };
}

const unitWord = (unit: DelayUnit, n: number) => (n === 1 ? unit.slice(0, -1) : unit);

/** "1 hour", "3 days": the same wording in the editor and the step summary. */
export function formatWait(delayDays?: number): string {
  if (!delayDays || delayDays <= 0) return '0 days';
  const { n, unit } = delayToWait(delayDays);
  return `${n} ${unitWord(unit, n)}`;
}

export function waitToDelay(n: number, unit: DelayUnit): number {
  return unit === 'days' ? n : Math.round((n / 24) * 100) / 100;
}

export interface TemplateStepEditorProps {
  /** 0-based position; the visible step number is `index + 1`. */
  index: number;
  step: TemplateStepDraft;
  onChange: (patch: Partial<TemplateStepDraft>) => void;
  /** Reports the subject caret so a merge tag can be spliced in where it was. */
  onCaret: (field: TemplateStepField, el: HTMLInputElement | HTMLTextAreaElement) => void;
  /** The body editor took focus. */
  onBodyFocus?: () => void;
  /** Inserts a `{tag}` into this step's subject or body. */
  onInsertTag?: (field: TemplateStepField, tag: string) => void;
  bodyHandle?: React.Ref<ComposerEditorHandle>;
  /** Bumped by the owner when it rewrites the body (a sign-off removed): the editor re-reads it. */
  bodySeed?: number;
  onDuplicate: () => void;
  /** Undefined for step 1 — it anchors the sequence and can't be deleted. */
  onDelete?: () => void;
  /** The template has one step only: it reads "Message", not "Step 1". */
  solo?: boolean;
  /** The final step of a sequence of 3+ gets a "last one from me" note. */
  isLastStep?: boolean;
  disabled?: boolean;
  /** False at the step cap: the menu's Duplicate would add a step the template can't hold. */
  canDuplicate?: boolean;
  /** Asks the AI to write this step; rejects when it fails. Absent: the AI button stays off. */
  onAiFill?: (instruction: string) => Promise<void>;
  /** Shown read-only: every field locked and no step actions. */
  readOnly?: boolean;
  /** First step only (U5): the "sent exactly as written" line under the subject. */
  subjectHelp?: string;
  /** Advisory, shown under the subject in amber. */
  subjectWarning?: string;
  /** Advisory under the body with a one-click fix (U3 sign-off). */
  signOff?: { message: string; removes?: string; onRemove: () => void };
}

export function TemplateStepEditor({
  index,
  step,
  onChange,
  onCaret,
  onBodyFocus,
  onInsertTag,
  bodyHandle,
  bodySeed = 0,
  onDuplicate,
  onDelete,
  solo = false,
  isLastStep = false,
  canDuplicate = true,
  onAiFill,
  disabled = false,
  readOnly = false,
  subjectHelp,
  subjectWarning,
  signOff,
}: TemplateStepEditorProps) {
  const uid = useId();
  const stepNo = index + 1;
  const isFirst = stepNo === 1;
  const linkNoteId = `${uid}-link-note`;
  const delayId = `${uid}-delay`;
  const sendTimeId = `${uid}-send-time`;
  const locked = disabled || readOnly;

  const numericDelay = step.delayDays === '' ? undefined : Number(step.delayDays);
  const [wait, setWait] = useState<{ n: number; unit: DelayUnit }>(() => delayToWait(numericDelay));
  const [collapsed, setCollapsed] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const caretProps = (field: TemplateStepField) => ({
    onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => onCaret(field, e.currentTarget),
    onClick: (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => onCaret(field, e.currentTarget),
    onKeyUp: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => onCaret(field, e.currentTarget),
    onSelect: (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => onCaret(field, e.currentTarget),
  });

  const title = solo ? 'Message' : `Step ${stepNo}`;
  const ToggleIcon = collapsed ? CaretDown : CaretUp;

  return (
    <>
      {!isFirst ? (
        <div className="flex justify-center">
          <div data-wait-pill className="flex flex-wrap items-center justify-center gap-2 rounded-full border border-border bg-card px-3 py-1.5">
            <Clock size={16} className="text-muted-foreground" />
            <Label htmlFor={delayId} className="text-body-sm-regular text-foreground">
              Wait
            </Label>
            <Input
              id={delayId}
              type="number"
              min={1}
              className="h-7 w-14 px-2 text-center"
              aria-label={`Step ${stepNo} delay`}
              value={wait.n}
              disabled={locked}
              onChange={(e) => {
                const n = Number(e.target.value);
                setWait((prev) => ({ ...prev, n }));
                onChange({ delayDays: String(waitToDelay(n, wait.unit)) });
              }}
            />
            <select
              aria-label={`Step ${stepNo} wait unit`}
              value={wait.unit}
              disabled={locked}
              onChange={(e) => {
                const unit = e.target.value as DelayUnit;
                setWait((prev) => ({ ...prev, unit }));
                // A send time only makes sense on a whole-day wait.
                onChange({ delayDays: String(waitToDelay(wait.n, unit)), ...(unit === 'hours' ? { sendTime: '' } : {}) });
              }}
              className="h-7 rounded-md border border-border bg-background px-2 text-body-sm-regular disabled:opacity-50"
            >
              <option value="days">{unitWord('days', wait.n)}</option>
              <option value="hours">{unitWord('hours', wait.n)}</option>
            </select>
            {wait.unit === 'days' ? (
              <>
              <span aria-hidden className="h-4 w-px bg-border" />
              <Label htmlFor={sendTimeId} className="text-body-sm-regular text-foreground">
                Send at
              </Label>
              <Input
                id={sendTimeId}
                type="time"
                className="h-7 w-28 px-2"
                aria-label={`Step ${stepNo} send time`}
                title="Time of day in the same time zone as the first email. Leave empty to send as soon as the wait is over."
                value={step.sendTime}
                disabled={locked}
                onChange={(e) => onChange({ sendTime: e.target.value })}
              />
              {step.sendTime && !locked ? (
                <button
                  type="button"
                  aria-label={`Clear step ${stepNo} send time`}
                  onClick={() => onChange({ sendTime: '' })}
                  className="flex size-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X size={12} />
                </button>
              ) : null}
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      <div data-step-card={stepNo} className="overflow-hidden rounded-xl border border-border bg-card">
        <div className={`flex items-center justify-between gap-3 px-4 py-3 ${collapsed ? '' : 'border-b border-border'}`}>
          <p className="flex items-center gap-2 text-body-sm-medium text-foreground">
            <Envelope size={18} />
            {title}
            {step.aiAssisted ? (
              <span
                title="AI helped write this step"
                className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700"
              >
                AI
              </span>
            ) : null}
            {isLastStep ? <span className="text-body-sm-regular font-normal text-muted-foreground">Last one from me on this</span> : null}
          </p>
          <div className="flex items-center gap-1">
            {readOnly ? null : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Step ${stepNo} actions`}
                    disabled={disabled}
                    className="flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                  >
                    <DotsThree size={18} weight="bold" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem disabled={!canDuplicate} onSelect={onDuplicate}>
                    <Copy /> Duplicate
                  </DropdownMenuItem>
                  {onDelete ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem variant="destructive" onSelect={onDelete}>
                        <Trash /> Delete
                      </DropdownMenuItem>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <button
              type="button"
              aria-label={`${collapsed ? 'Expand' : 'Collapse'} step ${stepNo}`}
              aria-expanded={!collapsed}
              onClick={() => setCollapsed((prev) => !prev)}
              className="flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <ToggleIcon size={16} />
            </button>
          </div>
        </div>

        <div hidden={collapsed} className="flex flex-col gap-3 p-3">
          {isFirst ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2 rounded-lg border border-input bg-background px-3">
                <span aria-hidden className="text-body-sm-regular text-muted-foreground">
                  Subject
                </span>
                <Input
                  value={step.subject}
                  disabled={locked}
                  aria-label={`Step ${stepNo} subject`}
                  className="h-11 min-w-0 flex-1 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                  onChange={(e) => {
                    onCaret('subject', e.currentTarget);
                    onChange({ subject: e.target.value });
                  }}
                  {...caretProps('subject')}
                />
                {readOnly || !onInsertTag ? null : (
                  <MergeTagMenu
                    label={`Insert merge tag into subject of step ${stepNo}`}
                    disabled={disabled}
                    onInsert={(tag) => onInsertTag('subject', tag)}
                  />
                )}
              </div>
              {readOnly ? null : <CharCounter label={`Subject of step ${stepNo}`} text={step.subject} limit={SUBJECT_LIMIT} />}
              {subjectWarning ? (
                <p role="status" className="text-caption-regular text-amber-800">
                  {subjectWarning}
                </p>
              ) : null}
              {subjectHelp ? <p className="text-caption-regular text-muted-foreground">{subjectHelp}</p> : null}
            </div>
          ) : (
            <p className="text-caption-regular text-muted-foreground">
              Sent in the same thread as the first email, so it has no subject of its own.
            </p>
          )}

          {aiOpen && onAiFill ? (
            <TemplateStepAiPanel stepNo={stepNo} onFill={onAiFill} onClose={() => setAiOpen(false)} />
          ) : null}

          <div className="flex flex-col gap-1.5">
            <div className="overflow-hidden rounded-lg border border-input" onFocusCapture={onBodyFocus}>
              <ComposerEditor
                key={bodySeed}
                handleRef={bodyHandle}
                ariaLabel={`Step ${stepNo} body`}
                autoFocus={false}
                liveRegion={false}
                allowLink={!isFirst}
                numberedList
                editable={!locked}
                ariaDescribedBy={isFirst ? linkNoteId : undefined}
                initialText={step.body}
                initialHtml={step.bodyHtml}
                placeholder="Write the email…"
                onChange={(text, html) => onChange({ body: text, bodyHtml: html })}
                onSubmitShortcut={() => undefined}
                renderHeader={(toolbar) =>
                  readOnly ? null : (
                    <div className="flex items-center gap-2 border-b border-border bg-background px-3 py-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        disabled={!onAiFill || disabled}
                        aria-label="Help me write"
                        aria-expanded={aiOpen}
                        title="Help me write"
                        onClick={() => setAiOpen((open) => !open)}
                        className="rounded-lg text-violet-600"
                      >
                        <Sparkle size={20} />
                      </Button>
                      <span aria-hidden className="h-4 w-px bg-border" />
                      {toolbar}
                      {onInsertTag ? (
                        <>
                          <span aria-hidden className="h-4 w-px bg-border" />
                          <MergeTagMenu
                            label={`Insert merge tag into body of step ${stepNo}`}
                            disabled={disabled}
                            onInsert={(tag) => onInsertTag('body', tag)}
                          />
                        </>
                      ) : null}
                    </div>
                  )
                }
              />
            </div>
            {readOnly ? null : <CharCounter label={`Step ${stepNo} body`} text={step.body} limit={BODY_LIMIT} />}
            {isFirst ? (
              <p id={linkNoteId} className="text-caption-regular text-muted-foreground">
                {FIRST_STEP_LINK_NOTE}
              </p>
            ) : null}
            {signOff && !readOnly ? (
              <p role="status" className="flex flex-wrap items-center gap-2 text-caption-regular text-amber-800">
                <span>{signOff.message}</span>
                <button
                  type="button"
                  onClick={signOff.onRemove}
                  disabled={disabled}
                  title={signOff.removes}
                  className="text-caption-medium underline underline-offset-2"
                  aria-label="Remove sign-off"
                >
                  Remove
                </button>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
